'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { ThreadSidebarPreferences } from '@/lib/thread-sidebar-cookie';
import type { SidebarSectionsPreference } from '@/lib/sidebar-sections-cookie';
import { THREAD_SIDEBAR_GEOMETRY } from '@/lib/sidebar-offset-boot';

const ServerSidebarPreferencesContext =
  createContext<ThreadSidebarPreferences | null>(null);
const ServerSidebarSectionsContext =
  createContext<SidebarSectionsPreference | null>(null);

export function ServerSidebarPreferencesProvider({
  value,
  sections = null,
  children,
}: {
  value: ThreadSidebarPreferences | null;
  /** Section disclosure the server rendered with (its cookie mirror). */
  sections?: SidebarSectionsPreference | null;
  children: ReactNode;
}) {
  return (
    <ServerSidebarPreferencesContext.Provider value={value}>
      <ServerSidebarSectionsContext.Provider value={sections}>
        {children}
      </ServerSidebarSectionsContext.Provider>
    </ServerSidebarPreferencesContext.Provider>
  );
}

/**
 * Section disclosure the server rendered the workspace sidebar with. Hydration
 * must start from it; localStorage reconciles afterwards (and normally agrees).
 */
export function useServerSidebarSections(): SidebarSectionsPreference | null {
  return useContext(ServerSidebarSectionsContext);
}

/**
 * Preferences the server rendered the thread sidebar with (from its cookie
 * mirror), for the sidebar that owns `storageKey`. The first client render
 * must match them; localStorage reconciles afterwards.
 */
export function useServerThreadSidebarPreferences(
  storageKey: string
): ThreadSidebarPreferences | null {
  const preferences = useContext(ServerSidebarPreferencesContext);
  const g = THREAD_SIDEBAR_GEOMETRY;
  return storageKey === g.collapsedStorageKey ||
    storageKey === g.widthStorageKey
    ? preferences
    : null;
}
